/** Manage a saved activity row's on-trip documents (immediate-sync API, keyed
 *  by User_Trip_Flow id): list / add link / upload file / reorder / delete.
 *  Independent of the tour draft: every action here happens at once (the dialog says so,
 *  the adds carry the immediate-action bolt, a delete asks first). */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Link, List, ListItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { ItineraryDocument } from '@/api/tours';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { IMMEDIATE_DESCRIPTION, ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';

/** An icon button whose action happens at once (not part of the draft). */
const IMMEDIATE = { 'aria-description': IMMEDIATE_DESCRIPTION } as Record<string, string>;

export const activityDocsKey = (utfId: number) => ['activity-docs', utfId] as const;

export function ActivityDocumentsDialog({
  open, utfId, canManage, onClose,
}: {
  open: boolean;
  utfId: number;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const key = activityDocsKey(utfId);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Creating server data asks first (an immediate action).
  const [confirmLink, setConfirmLink] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => toursApi.listActivityDocuments(utfId),
    enabled: open,
  });
  const docs = data?.results ?? [];

  const onErr = (e: unknown) =>
    snackbar.show(e instanceof ConsoleApiError ? e.message : 'Document action failed.', 'error');
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const addLink = useMutation({
    mutationFn: () => toursApi.createActivityDocumentLink(utfId, { name: name.trim() || url.trim(), url: url.trim() }),
    onSuccess: () => { setName(''); setUrl(''); refresh(); }, onError: onErr,
  });
  const upload = useMutation({
    mutationFn: (file: File) => toursApi.uploadActivityDocument(utfId, file, { name: file.name }),
    onSuccess: refresh, onError: onErr,
  });
  const remove = useMutation({
    mutationFn: (docId: number) => toursApi.removeActivityDocument(utfId, docId),
    onSuccess: () => { setConfirmDelete(null); void refresh(); }, onError: onErr,
  });
  const reorder = useMutation({
    mutationFn: (ids: number[]) => toursApi.reorderActivityDocuments(utfId, ids),
    onSuccess: refresh, onError: onErr,
  });

  const move = (index: number, delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= docs.length) return;
    const ids = docs.map((d) => d.id);
    [ids[index], ids[to]] = [ids[to]!, ids[index]!];
    reorder.mutate(ids);
  };

  const busy = addLink.isPending || upload.isPending || remove.isPending || reorder.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>On-trip documents</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" icon={<BoltOutlinedIcon fontSize="inherit" />} sx={{ mb: 2, py: 0 }}>
          Changes here happen at once — they are not part of the tour’s draft.
        </Alert>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            {docs.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No documents yet.</Alert>}
            <List dense disablePadding>
              {docs.map((doc: ItineraryDocument, i) => (
                <ListItem key={doc.id} divider disableGutters
                  secondaryAction={
                    <Stack direction="row">
                      {/* Each of these happens at once — said in its name and description. */}
                      <Tooltip title="Move up now"><span>
                        <IconButton size="small" disabled={!canManage || i === 0} onClick={() => move(i, -1)}
                          aria-label={`Move ${doc.name} up now`} {...IMMEDIATE}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title="Move down now"><span>
                        <IconButton size="small" disabled={!canManage || i === docs.length - 1} onClick={() => move(i, 1)}
                          aria-label={`Move ${doc.name} down now`} {...IMMEDIATE}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      {confirmDelete === doc.id ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption">Delete {doc.name} now?</Typography>
                          <ImmediateActionButton color="error" disabled={busy} onClick={() => remove.mutate(doc.id)}>
                            Delete now
                          </ImmediateActionButton>
                          <Button size="small" onClick={() => setConfirmDelete(null)}>Keep</Button>
                        </Stack>
                      ) : (
                        <Tooltip title="Delete now"><span>
                          <IconButton size="small" color="error" disabled={!canManage} aria-label={`Delete ${doc.name} now`} {...IMMEDIATE}
                            onClick={() => setConfirmDelete(doc.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span></Tooltip>
                      )}
                    </Stack>
                  }
                >
                  <Box sx={{ minWidth: 0, pr: 6 }}>
                    <Typography variant="body2" noWrap>{doc.name}</Typography>
                    <ValueActions
                      actions={doc.link || doc.url ? [act.copy((doc.link || doc.url)!, 'Copy URL')] : []}
                    >
                      <Link href={doc.link || doc.url || undefined} target="_blank" rel="noopener noreferrer"
                        variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {doc.type === 1 ? doc.url : (doc.content_type ?? 'file')}
                      </Link>
                    </ValueActions>
                  </Box>
                </ListItem>
              ))}
            </List>

            {canManage && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Add</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ width: 160 }} />
                  <TextField size="small" label="URL" value={url} onChange={(e) => setUrl(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
                  <ImmediateActionButton variant="outlined"
                    disabled={!url.trim() || busy || confirmLink} onClick={() => setConfirmLink(true)}>
                    Add link now
                  </ImmediateActionButton>
                </Stack>
                {confirmLink && (
                  <Confirm
                    text={`Add “${name.trim() || url.trim()}” to this activity now? Guests see it at once.`}
                    action="Add link"
                    busy={busy}
                    onConfirm={() => { setConfirmLink(false); addLink.mutate(); }}
                    onCancel={() => setConfirmLink(false)}
                  />
                )}
                <ImmediateActionButton variant="outlined" disabled={busy || pendingFile !== null} onClick={() => fileInput.current?.click()}>
                  Upload file now
                </ImmediateActionButton>
                <input ref={fileInput} hidden type="file" data-document-input
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingFile(f); e.target.value = ''; }} />
                {pendingFile && (
                  <Confirm
                    text={`Upload ${pendingFile.name} to this activity now? Guests see it at once.`}
                    action="Upload"
                    busy={busy}
                    onConfirm={() => { const f = pendingFile; setPendingFile(null); upload.mutate(f); }}
                    onCancel={() => setPendingFile(null)}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Confirm({ text, action, busy, onConfirm, onCancel }: { text: string; action: string; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" role="group" aria-label={text} sx={{ my: 1 }} data-document-confirm>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {text}
      </Typography>
      <Button size="small" variant="contained" disabled={busy} onClick={onConfirm}>
        {action}
      </Button>
      <Button size="small" onClick={onCancel}>
        Cancel
      </Button>
    </Stack>
  );
}
